package scroll

import common.dto.ElementType
import common.dto.Vec3d
import ext.set
import kotlinx.serialization.Serializable
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Represents an element in the scene at a certain keyframe. The element can be of a certain
 * [type] and has a [pos] and [rot] in 3D space.
 *
 * By default, the element only exists in the keyframe it is defined in. If the element should
 * persist across multiple keyframes, an [id] must be specified in this and in previous/succeeding
 * keyframes.
 *
 * @param id by default, a random UUID is generated. This is important for "persisting" elements during
 * a keyframe with a range of scroll progress. If the element should persist across multiple keyframes,
 * the same id must be used in all keyframes.
 *
 */
@OptIn(ExperimentalUuidApi::class)
@Serializable
data class ScrollElement(
    val type: ElementType,
    val pos: Vec3d = Vec3d(),
    val rot: Vec3d = Vec3d(),
    val id: String = Uuid.random().toHexString(),
    // todo dynamic default offset based on maybe z position? depending on how much the camera can see
    val introOffset: Vec3d = Vec3d(x = 500.0),
    val outroOffset: Vec3d = Vec3d(x = -500.0),
) {
    override fun equals(other: Any?) = other is ScrollElement && other.type == type && other.id == id

    override fun hashCode() = 31 * type.hashCode() + id.hashCode()

    /**
     * Instantiates a model of the given type *with* the corresponding position and rotation.
     */
    fun instantiate() = type.cloneModel().apply {
        position.set(pos)
        rotation.set(rot)
    }
}